import { useState } from "react";
import { REAL_PHOTOS, type RealPhotoCategory } from "@/lib/real-photos";

type Props = {
  category: RealPhotoCategory;
  /** Local bundled image used as the final fallback if every CDN source fails. */
  fallback: string;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
};

/**
 * Renders a real (photoreal) image with a graceful fallback chain:
 * curated Unsplash photographs → next Unsplash source → bundled local asset.
 *
 * This guarantees the UI never shows an obviously AI-looking image: every
 * primary source is hand-curated real photography, and the bundled fallback
 * is the production-safe last resort.
 */
export function RealPhoto({
  category,
  fallback,
  alt,
  className,
  width,
  height,
  loading = "lazy",
}: Props) {
  const set = REAL_PHOTOS[category];
  const chain = [...set.sources, fallback];
  const [index, setIndex] = useState(0);

  return (
    <img
      src={chain[index]}
      alt={alt ?? set.alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      className={className}
      onError={() => {
        if (index < chain.length - 1) setIndex((i) => i + 1);
      }}
    />
  );
}
